/**
 * What a recipient actually receives — the copy layer for the sentence slice 11
 * had to take back.
 *
 * Slice 4 earned a strong claim and printed it in several places: **an email
 * Wend sends makes no network request when it is opened.** It was not
 * marketing. It was the reason MJML's injected Google Fonts `<link>` was
 * stripped out of the compiler, and the reason an inline image is a CID part
 * carrying its own bytes rather than a URL the reader's mail client has to go
 * and fetch. Somebody chose it, and paid for it in compiler code.
 *
 * With open tracking on, Amazon injects a 1×1 pixel; with click tracking on, it
 * rewrites every link. Both happen **server-side, after the handover** — Wend's
 * bytes do not change — so the claim is now false for that workspace while
 * remaining true, and provably true, for every workspace that left tracking off.
 * This module is the single place that decides which of those two sentences a
 * screen prints.
 *
 * **Slice 21 made the tracking-off half conditional again, on the deployment.**
 * Where `TEMPLATE_ASSET_PUBLIC_BASE_URL` is set, a picture is no longer a CID
 * part: the reader's mail app fetches it from Wend's image host when the
 * message is opened, carrying their IP address, whatever tracking is set to. So
 * "nothing in this email reports back … nothing for a mail app to go and fetch"
 * is true only where pictures are embedded. {@link whatTheMessageCarries} is told
 * the delivery mode (`Template.imageDelivery`) and says the version that is true;
 * the first paragraph it prints, `disclosure.off`, is already chosen by the API
 * for the same reason (`trackingOffDisclosure` in `@wend/shared`).
 *
 * ## Three decisions, and the third is the one worth reading
 *
 * **1. There is no default.** `TrackingKnowledge` has an explicit *unknown* arm
 * and every function handles it as its own case with its own words. The obvious
 * alternative — treat a failed or in-flight read as "tracking off", since off is
 * what almost every workspace is — reinstates the exact bug this slice fixes:
 * the guess is right for the majority and wrong for precisely the people who
 * switched tracking on and are the only ones the sentence can hurt. A screen
 * that cannot read the setting says so. It is one more sentence and it is never
 * a lie.
 *
 * **2. The branch is `sendCarriesEngagement`, not `tracksEngagement`.**
 * This is the important one. A workspace can have the switch on and have
 * nothing provisioned — a provisioning run that has not finished, or one that
 * failed — and in that state the send does not name the tracking set, and
 * **Amazon injects nothing**. Branching on `tracksEngagement` would tell that
 * tenant their mail carries a pixel when it demonstrably does not, which is the
 * same species of lie as the one this slice is repairing, just pointed the other
 * way. So the copy branches on the send path's own decision, and the screen and
 * the transport cannot disagree about what a message carries.
 *
 * It used to be `configurationSetNameForSend(...) === null`, when naming a set
 * and being tracked were one fact. Slice 19 separated them: a tracking-off send
 * now names the delivery reports set, which modifies nothing. Asked the old way,
 * every workspace that set up delivery reports would have been told its mail
 * carries a tracking pixel. `sendCarriesEngagement` is the half of that function
 * which is about the recipient, and it is what the log line and the mid-run
 * narrowing ask as well.
 *
 * **3. The preview's gap is stated, never papered over.** Slice 4's other
 * promise was that the preview is the compile — same function, same bytes, and
 * a verify script proves the two strings match. That comparison is still exact
 * and is not weakened here. What changed is that with click tracking on, the
 * links a *recipient* sees are not the links the author wrote, and Wend cannot
 * show them, because the rewritten URL does not exist until Amazon makes it.
 * `previewGap` says that in words. It would have been easy to render a
 * plausible `https://links.acmebank.in/CL0/...` beside each link and call the
 * preview complete; that is a fabrication, it would be wrong in every character
 * after the host, and a preview that invents what a recipient sees is a worse
 * failure than a preview that admits a gap.
 *
 * ## What this module is not
 *
 * It states no rate, shows no count and reads no engagement figure. Open rate
 * is directional, it belongs beside the caveats `ENGAGEMENT_CAVEAT_MESSAGES`
 * carries, and none of that belongs on a screen whose job is to describe what
 * one message will contain. This file answers one question — *what will the
 * person who receives this actually get?* — and refuses the adjacent ones.
 */
import {
  sendCarriesEngagement,
  tracksEngagement,
  type TemplateImageDelivery,
  type TrackingResponse,
  type TrackingSettings,
} from '@wend/shared';
import type { CalloutTone } from '@wend/ui';

/* -------------------------------------------------------------------------- */
/* Knowing, or not knowing                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What a screen knows about this workspace's tracking. **Three arms, and the two
 * that are not `known` are kept apart on purpose.**
 *
 *   reading      the first read has not answered yet. Say nothing at all — a
 *                screen that has not asked is not a screen with bad news
 *   unavailable  the read failed, or this role may not make it. Say so
 *   known        the settings, and the disclosure text that came with them
 *
 * Collapsing `reading` into `unavailable` would flash "Wend could not read your
 * tracking settings" on every visit to the editor for as long as the request
 * takes, which trains people to ignore the sentence that means it. Collapsing
 * `unavailable` into `reading` would leave a silent screen next to a preview,
 * which reads as "there is nothing to say here" — and that is the old promise
 * being made by omission.
 */
export type TrackingKnowledge =
  | { readonly known: 'reading' }
  | { readonly known: 'unavailable' }
  | { readonly known: 'yes'; readonly tracking: TrackingResponse };

/** Before anything has been asked. */
export const TRACKING_READING: TrackingKnowledge = { known: 'reading' };

/**
 * The adapter from `useAsyncData` to the union above.
 *
 * It takes the two fields it needs structurally rather than the whole
 * `AsyncData<TrackingResponse>`, so a caller holding the settings some other way
 * — a route loader, a test — can build one without inventing a `reload`. It
 * deliberately does **not** take the error: every reason a read failed produces
 * the same sentence for a reader, and passing the error in would invite a
 * component to start explaining an HTTP status in the middle of a paragraph
 * about somebody's inbox.
 *
 * `data` surviving a refetch is why `loadingFirst` is the flag rather than
 * `status === 'loading'`: a background reload over settings already on screen
 * keeps describing those settings instead of falling silent.
 */
export function trackingKnowledge(read: {
  data: TrackingResponse | null;
  loadingFirst: boolean;
}): TrackingKnowledge {
  if (read.data !== null) return { known: 'yes', tracking: read.data };
  return read.loadingFirst ? TRACKING_READING : { known: 'unavailable' };
}

/* -------------------------------------------------------------------------- */
/* What is actually happening to a message right now                           */
/* -------------------------------------------------------------------------- */

/**
 * The live state of a workspace's tracking, as opposed to what its switches say.
 *
 *   reading      nothing has been asked yet
 *   unknown      the setting could not be read
 *   none         nothing is added to a message — slice 4's sentence still holds
 *   pending      the switches are on and nothing is provisioned, so **nothing is
 *                added yet**. A real state, and the one a boolean would hide
 *   open         a pixel, no link rewriting
 *   click        link rewriting, no pixel
 *   both         a pixel and link rewriting
 */
export type RecipientTrackingState =
  | 'reading'
  | 'unknown'
  | 'none'
  | 'pending'
  | 'open'
  | 'click'
  | 'both';

/**
 * Which of the seven a workspace is in, decided by the send path's own predicate.
 *
 * `sendCarriesEngagement` being false is not a detail this function interprets
 * — it *is* the send not naming the tracking set, and SES adds nothing to a
 * message sent through no set, or through the delivery reports set, which Wend
 * names only while nothing on it tracks. So false here means nothing Wend
 * configures adds to the mail, whatever the switches read, and `pending` is how that is said out
 * loud instead of being rounded to `none` (which would hide a broken
 * provisioning run) or to `open` (which would describe a pixel that is not
 * there). Whether delivery reports come back is a different question, and not
 * this function's: it describes what a recipient receives.
 */
export function recipientTrackingState(knowledge: TrackingKnowledge): RecipientTrackingState {
  if (knowledge.known === 'reading') return 'reading';
  if (knowledge.known === 'unavailable') return 'unknown';

  const settings = knowledge.tracking.settings;
  if (!sendCarriesEngagement(settings)) {
    return tracksEngagement(settings) ? 'pending' : 'none';
  }

  if (settings.open && settings.click) return 'both';
  return settings.click ? 'click' : 'open';
}

/**
 * True only when Wend can still make slice 4's promise for this workspace.
 *
 * `pending` counts, because in that state the message really does carry
 * nothing. It is the honest answer and it is a temporary one, which is why the
 * copy for `pending` says both halves rather than only the reassuring one.
 */
export function makesNoRequestOnOpen(knowledge: TrackingKnowledge): boolean {
  const state = recipientTrackingState(knowledge);
  return state === 'none' || state === 'pending';
}

/**
 * True only when Amazon is definitely adding something to this workspace's mail.
 *
 * **Not the negation of `makesNoRequestOnOpen`, and that is the point.** Between
 * them sit `reading` and `unknown`, where both are false and a screen is
 * entitled to claim neither direction. A component branching on `!makesNo…`
 * would print "Amazon changes this message after Wend hands it over" during the
 * half-second before the settings arrive, which is a sentence about somebody's
 * mail asserted on the strength of a request that has not answered yet.
 */
export function sesModifiesMessage(knowledge: TrackingKnowledge): boolean {
  const state = recipientTrackingState(knowledge);
  return state === 'open' || state === 'click' || state === 'both';
}

/**
 * The redirect subdomain a recipient will see in place of the author's links, or
 * `null` when nothing is being rewritten.
 *
 * It is read from the `click: true` arm, where `redirectDomain` is the *verified*
 * shape by type — decision 3 of this slice, held by the union rather than by a
 * check here. There is no branch for "click on, domain unverified" because the
 * contract cannot express one.
 */
export function redirectDomainInUse(knowledge: TrackingKnowledge): string | null {
  if (knowledge.known !== 'yes') return null;
  const settings: TrackingSettings = knowledge.tracking.settings;
  if (!settings.click) return null;
  if (!sendCarriesEngagement(settings)) return null;
  return settings.redirectDomain.domain;
}

/* -------------------------------------------------------------------------- */
/* The notices                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * One block of copy: a tone, a heading, and paragraphs in reading order.
 *
 * Data rather than JSX, for the reason `template-display.ts` is data: the
 * editor, the preview and the test-send panel must not be able to describe the
 * same message three different ways, and a component that took a `variant` prop
 * would grow a fourth description the first time one screen needed a different
 * layout.
 */
export interface RecipientNotice {
  readonly tone: CalloutTone;
  readonly title: string;
  readonly paragraphs: readonly string[];
  /**
   * Slice 29. True on the two arms that describe a SETUP problem rather than a
   * recipient experience, and false everywhere else.
   *
   * The distinction is the one the whole slice turns on. *"Opening this email
   * makes one request"* is about the email the writer is composing, so it stays
   * on the compose surface in full. *"Tracking is switched on, but nothing is
   * tracking yet"* is about a half-finished configuration set, which is repaired
   * on Integrations and nowhere else — so the writer gets the one sentence that
   * affects their draft and a way to reach the repair, instead of eighty words
   * about SES.
   */
  readonly pointsAtIntegrations?: boolean;
}

/**
 * **What a message sent from this workspace carries.** The block that replaces
 * slice 4's fixed sentence everywhere it was printed.
 *
 * The `none` arm prints `disclosure.off` verbatim — the API's own words, carried
 * on the response so the sentence a tenant is shown here is the same string the
 * audit entry recorded when they last touched the switch. A workspace with
 * tracking off is *entitled* to see the strong claim stated, not to see silence
 * where the claim used to be; that is why `off` exists in the disclosure at all.
 *
 * The mechanism sentence appended to it — CID images, no remote fonts — is
 * Wend's half of the promise and is why the claim was true in the first place.
 * It is printed only where pictures are still CID images; where they are hosted
 * the notice says instead that the pictures are the one thing fetched.
 * It stays local to this file rather than moving into the contract because it
 * describes what the *compiler* does, and the contract describes what SES does.
 *
 * `null` while the settings are still being read, and only then. A screen that
 * has not finished asking should show nothing rather than an answer it is about
 * to replace.
 */
export function whatTheMessageCarries(
  knowledge: TrackingKnowledge,
  /**
   * How this deployment carries the pictures in an email — `Template.imageDelivery`.
   * It changes the tracking-off notice only: its title and its "nothing to fetch"
   * sentence are true where pictures are embedded and false where they are
   * hosted. `null` (the template view did not say) prints neither mechanism
   * sentence rather than guessing, and keeps the API's own `disclosure.off`,
   * which was chosen knowing the mode.
   */
  imageDelivery: TemplateImageDelivery | null,
): RecipientNotice | null {
  if (knowledge.known === 'reading') return null;

  if (knowledge.known === 'unavailable') {
    return {
      tone: 'warn',
      title: 'Wend could not read this workspace’s tracking settings',
      /*
       * SLICE 29: THE COMPOSE SURFACE IS A WORKING SURFACE.
       *
       * This arm and `pending` below were the two longest blocks on the template
       * editor — 78 and 82 words — and neither changes a word of the email the
       * person is writing. One is a failed configuration read; the other is a
       * half-finished SES configuration set. Both are repaired on Integrations
       * and nowhere else.
       *
       * What a writer needs is the one sentence that affects their draft, and a
       * way to get to the repair. `pointsAtIntegrations` is what puts the link
       * there; the removed paragraphs are the operator's copy and they say the
       * same things the Email tab says beside the switch.
       */
      pointsAtIntegrations: true,
      paragraphs: [
        'What a recipient’s mail app does when it opens this message depends on a setting that could not be read just now. There are two opposite true sentences here — one for a workspace that tracks opens and clicks and one for a workspace that does not — so rather than print the wrong one, this panel prints neither.',
      ],
    };
  }

  const state = recipientTrackingState(knowledge);
  const { disclosure } = knowledge.tracking;

  if (state === 'none') {
    /*
     * `===` against each named mode. The embedded arm is word for word what this
     * notice said before pictures could be hosted; the hosted arm drops the claim
     * that nothing is fetched, because a picture is; and an unknown mode says only
     * the API's sentence, which was chosen knowing the mode.
     */
    if (imageDelivery === 'hosted') {
      return {
        tone: 'good',
        title: 'No tracking in this email — its pictures are fetched when it is opened',
        paragraphs: [
          disclosure.off,
          'There are no remote fonts or remote styles. The pictures are the one thing a mail app fetches: this deployment serves them from Wend’s image host rather than carrying them inside the message, so a reader whose mail app shows pictures makes that request when they open it.',
        ],
      };
    }
    if (imageDelivery === 'embedded') {
      return {
        tone: 'good',
        title: 'Nothing in this email reports back',
        paragraphs: [
          disclosure.off,
          'That is not an accident of the design: images in the body travel with the message as attached parts rather than as links to a server, and there are no remote fonts or remote styles, so there is nothing in it for a mail app to go and fetch.',
        ],
      };
    }
    return { tone: 'good', title: 'No tracking in this email', paragraphs: [disclosure.off] };
  }

  if (state === 'pending') {
    return {
      tone: 'warn',
      title: 'Tracking is switched on, but nothing is tracking yet',
      /* See the `unavailable` arm above for why this one is a clause and a
         link rather than eighty-two words. */
      pointsAtIntegrations: true,
      paragraphs: [
        'This workspace has asked for tracking, and Amazon has nothing set up to do it — so messages leaving right now still carry no pixel and no rewritten links, exactly as if tracking were off.',
        'That will change the moment the setup finishes, and this panel will say so. Until then, do not tell anyone their opens are being counted, and do not tell them they are not: finish the setup in Integrations and read this panel again.',
      ],
    };
  }

  const domain = redirectDomainInUse(knowledge);
  const linkSentence =
    domain === null
      ? null
      : `Recipients will see links on ${domain} rather than the addresses you typed here. Amazon rewrites them as it sends, after Wend has handed the message over — which is why this editor shows the links you wrote and cannot show the ones that will arrive.`;

  if (state === 'open') {
    return {
      tone: 'info',
      title: 'Opening this email makes one request',
      paragraphs: [
        disclosure.open,
        'Wend does not put the pixel in the email. Amazon adds it as it sends, because this workspace has open tracking on — so you will not find it in the preview or in the stored HTML, and the bytes Wend hands over are still exactly the bytes you can see.',
      ],
    };
  }

  if (state === 'click') {
    return {
      tone: 'info',
      title: 'Every link in this email is rewritten',
      paragraphs: [disclosure.click, ...(linkSentence === null ? [] : [linkSentence])],
    };
  }

  return {
    tone: 'info',
    title: 'This email carries a tracking pixel, and every link in it is rewritten',
    paragraphs: [
      disclosure.open,
      disclosure.click,
      ...(linkSentence === null ? [] : [linkSentence]),
      'Both are added by Amazon as it sends, not by Wend as it compiles — so neither is visible anywhere in this editor.',
    ],
  };
}

/**
 * **The preview's honest gap**, or `null` when there is nothing to admit.
 *
 * `null` for a workspace that tracks nothing is the point: the preview *is* the
 * email in that case, byte for byte, and adding a caveat where there is no
 * caveat would teach people to ignore the one that matters.
 *
 * The click arm is the one this exists for. Slice 4's guarantee was literal —
 * the preview goes through the compile function that produced the stored HTML,
 * and a verify script compares the two strings. That guarantee is untouched and
 * still worth having. What it no longer covers is the *link a recipient hovers*,
 * because Amazon replaces every href after the handover. The gap is stated
 * rather than filled with a plausible-looking rewritten URL, because a
 * fabricated preview is wrong in every character after the hostname and teaches
 * an author to trust a string no recipient will ever be sent.
 */
export function previewGap(knowledge: TrackingKnowledge): RecipientNotice | null {
  const state = recipientTrackingState(knowledge);

  if (state === 'none' || state === 'reading') return null;

  if (state === 'unknown') {
    return {
      tone: 'warn',
      title: 'This preview is what Wend sends — whether Amazon adds to it is unknown',
      paragraphs: [
        'The comparison above is exact: these are the bytes Wend hands to Amazon. Whether Amazon then inserts a tracking pixel or rewrites the links depends on this workspace’s tracking settings, which could not be read just now.',
      ],
    };
  }

  if (state === 'pending') {
    return {
      tone: 'info',
      title: 'This preview is what a recipient gets — for now',
      paragraphs: [
        'Tracking is switched on for this workspace but is not set up at Amazon’s end yet, so nothing is being added to messages and this preview is complete. When the setup finishes, Amazon will start inserting a pixel or rewriting links and this panel will say which.',
      ],
    };
  }

  const domain = redirectDomainInUse(knowledge);

  if (state === 'open') {
    return {
      tone: 'info',
      title: 'Amazon adds one thing to this that the preview cannot show',
      paragraphs: [
        'A 1×1 tracking pixel is inserted into every HTML message as Amazon sends it. It is invisible to a reader and it is not in the bytes above — the comparison is still exact for everything Wend controls, which is everything except the pixel.',
      ],
    };
  }

  const linkParagraph =
    domain === null
      ? 'Every link above is the link you wrote, and every one of them will be replaced. Amazon rewrites each href as it sends so that a click passes through a redirect first, and the rewritten address does not exist until then.'
      : `Every link above is the link you wrote, and every one of them will be replaced. Amazon rewrites each href as it sends so that a click passes through ${domain} first, and the rewritten address does not exist until then — Wend cannot show it here, and will not invent one that looks like it.`;

  return {
    tone: 'info',
    title:
      state === 'both'
        ? 'The links above are not the links a recipient will see, and a pixel is added'
        : 'The links above are not the links a recipient will see',
    paragraphs:
      state === 'both'
        ? [
            linkParagraph,
            'A 1×1 tracking pixel is inserted at the same moment, and is likewise absent from the bytes above.',
          ]
        : [linkParagraph],
  };
}

/**
 * The one line the test-send panel adds — the *whole* of its contribution.
 *
 * A test send is the only place in the product where the person asking "what
 * does a recipient actually get" can simply look — which makes it the right
 * place to point at the arriving message rather than to keep describing it.
 *
 * **Which is why the panel prints this and not `whatTheMessageCarries` as
 * well.** The test-send panel sits at the bottom of the same scroll whose top
 * already carries that notice, so rendering it again there would put an
 * identical callout on one page twice. Description happens once, at the top;
 * this is the sentence that could only belong beside a Send button.
 *
 * **Every sentence here is about where to look, and none of them asserts what
 * this particular message will carry.** That is not vagueness, it is the only
 * honest shape available: whether a *test* send is claimed by the workspace's
 * configuration set is a decision in `test-send.service.ts`, not something this
 * screen can observe, and a panel that told somebody "this test carries a pixel"
 * would be making a claim about API code it cannot see — the same category of
 * mistake as the promise this whole module exists to retract. "Wend cannot show
 * you the pixel, so look in the message" is true whichever way that decision
 * goes, and a test send exists precisely so the answer need not be taken on
 * trust.
 */
export function testSendCheck(knowledge: TrackingKnowledge): string | null {
  const state = recipientTrackingState(knowledge);
  if (state === 'none' || state === 'unknown' || state === 'reading') return null;
  if (state === 'pending') {
    return 'Nothing is being added to this workspace’s mail yet, so a test sent now arrives exactly as it was written. Send another once the setup has finished if you want to see the difference for yourself.';
  }
  return state === 'open'
    ? 'Wend cannot show you the pixel anywhere in this editor, because Wend does not put it there. A message in an inbox is the only place to look for it — view the source of the one that arrives.'
    : 'Wend cannot show you a rewritten link anywhere in this editor, because it does not exist until Amazon makes it. The message that arrives is the only place to see one — open it and hover a link.';
}
